/**
 * Moore Above Avg Blades -- Shopify Theme JS
 * Pure vanilla JS, no dependencies, no build step.
 * Loaded via <script src="{{ 'theme.js' | asset_url }}" defer></script>
 */

(function () {
  'use strict';

  /* ===================================================================
   * 1. Header Scroll State
   * Source: site-chrome.tsx lines 86-96
   * =================================================================== */
  function initHeaderScroll() {
    const header = document.querySelector('[data-header]');
    if (!header) return;
    const onScroll = () => {
      header.classList.toggle('header--scrolled', window.scrollY > 20);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  /* ===================================================================
   * 2. Mobile Menu
   * Source: mobile-menu.tsx
   * =================================================================== */
  function initMobileMenu() {
    const toggle = document.querySelector('[data-mobile-menu-toggle]');
    const menu = document.querySelector('[data-mobile-menu]');
    if (!toggle || !menu) return;

    let isOpen = false;
    let prevOverflow = '';

    const open = () => {
      isOpen = true;
      prevOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      menu.classList.add('mobile-menu--open');
      toggle.setAttribute('aria-expanded', 'true');

      // Stagger nav items via CSS transition-delay
      const items = menu.querySelectorAll('[data-mobile-menu-item]');
      items.forEach((item, i) => {
        item.style.transitionDelay = `${100 + i * 60}ms`;
      });
    };

    const close = () => {
      isOpen = false;
      document.body.style.overflow = prevOverflow;
      menu.classList.remove('mobile-menu--open');
      toggle.setAttribute('aria-expanded', 'false');

      const items = menu.querySelectorAll('[data-mobile-menu-item]');
      items.forEach((item) => {
        item.style.transitionDelay = '0ms';
      });
    };

    toggle.addEventListener('click', () => {
      isOpen ? close() : open();
    });

    // Escape key closes
    window.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && isOpen) close();
    });

    // Close on nav link click within menu
    menu.addEventListener('click', (e) => {
      if (e.target.closest('a[href]')) close();
    });
  }

  /* ===================================================================
   * 3. Accordion
   * Source: accordion.tsx
   * Only-one-open within a group; first item with data-accordion-open
   * starts expanded.
   * =================================================================== */
  function initAccordions(scope) {
    const root = scope || document;

    // Initialize default-open items
    root.querySelectorAll('[data-accordion-open]').forEach((trigger) => {
      trigger.setAttribute('aria-expanded', 'true');
      const panel = trigger.closest('[data-accordion-item]')
        ?.querySelector('[data-accordion-panel]');
      if (panel) panel.hidden = false;
    });

    // Event delegation
    root.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-accordion-trigger]');
      if (!trigger) return;

      const expanded = trigger.getAttribute('aria-expanded') === 'true';
      const group = trigger.closest('[data-accordion-group]');

      // If inside a group, close all others first
      if (group) {
        group.querySelectorAll('[data-accordion-trigger]').forEach((t) => {
          t.setAttribute('aria-expanded', 'false');
          const p = t.closest('[data-accordion-item]')
            ?.querySelector('[data-accordion-panel]');
          if (p) p.hidden = true;
        });
      }

      // Toggle the clicked item (if it was open and we just closed it, leave closed)
      if (!expanded) {
        trigger.setAttribute('aria-expanded', 'true');
        const panel = trigger.closest('[data-accordion-item]')
          ?.querySelector('[data-accordion-panel]');
        if (panel) panel.hidden = false;
      }
    });
  }

  /* ===================================================================
   * 4. Reveal on Scroll (IntersectionObserver)
   * Source: motion-helpers.tsx Reveal component
   * =================================================================== */
  function initReveal(scope) {
    const root = scope || document;
    const els = root.querySelectorAll('[data-reveal]');
    if (!els.length) return;

    // Theme editor, reduced-motion users, or browsers without IntersectionObserver: show everything.
    if (
      (window.Shopify && window.Shopify.designMode) ||
      !('IntersectionObserver' in window) ||
      window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      els.forEach((el) => el.classList.add('is-visible'));
      return;
    }
    // Safety net: never leave content hidden if the observer doesn't fire.
    setTimeout(() => els.forEach((el) => el.classList.add('is-visible')), 4000);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const el = entry.target;
          const delay = el.getAttribute('data-reveal-delay');
          if (delay) el.style.transitionDelay = delay + 'ms';
          el.classList.add('is-visible');
          observer.unobserve(el);
        });
      },
      { rootMargin: '-80px' }
    );

    els.forEach((el) => observer.observe(el));
  }

  /* ===================================================================
   * 5. Counter Animation
   * Source: motion-helpers.tsx Counter component
   * =================================================================== */
  function initCounters(scope) {
    const root = scope || document;
    const counters = root.querySelectorAll('[data-counter-to], [data-counter-display]');
    if (!counters.length) return;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        observer.unobserve(entry.target);
        animateCounter(entry.target);
      });
    });

    counters.forEach((el) => observer.observe(el));
  }

  function animateCounter(el) {
    const display = el.getAttribute('data-counter-display');
    if (display) {
      el.textContent = display;
      return;
    }

    const to = parseFloat(el.getAttribute('data-counter-to')) || 0;
    const suffix = el.getAttribute('data-counter-suffix') || '';
    const duration = 1600; // 1.6s
    let start = null;

    function step(ts) {
      if (!start) start = ts;
      const elapsed = ts - start;
      // Ease-out: 1 - (1 - t)^3
      const t = Math.min(elapsed / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      el.textContent = Math.round(eased * to) + suffix;
      if (t < 1) requestAnimationFrame(step);
    }

    requestAnimationFrame(step);
  }

  /* ===================================================================
   * 6. Product Gallery
   * Source: product.tsx ProductGallery (lines 129-167)
   * =================================================================== */
  function initProductGallery(scope) {
    const root = scope || document;
    const thumbs = root.querySelectorAll('[data-gallery-thumb]');
    if (!thumbs.length) return;

    const images = root.querySelectorAll('.product-gallery__image');

    thumbs.forEach((btn) => {
      btn.addEventListener('click', () => {
        const idx = btn.getAttribute('data-gallery-thumb');

        // Update thumbnails
        thumbs.forEach((t) => t.setAttribute('aria-pressed', 'false'));
        btn.setAttribute('aria-pressed', 'true');

        // Swap images
        images.forEach((img, i) => {
          const show = String(i) === idx;
          img.classList.toggle('product-gallery__image--active', show);
          img.hidden = !show;
        });
      });
    });
  }

  /* ===================================================================
   * 8. Call Button QR Popup
   * Source: call-button.tsx
   * =================================================================== */
  function initCallButtons(scope) {
    const root = scope || document;
    const isDesktop = window.matchMedia('(min-width: 768px)').matches;
    if (!isDesktop) return; // Mobile: plain tel: links, no JS needed

    root.querySelectorAll('[data-call-button-desktop]').forEach((wrapper) => {
      const popup = wrapper.querySelector('.call-qr-popup');
      if (!popup) return;

      let timer = null;

      const show = () => {
        clearTimeout(timer);
        popup.classList.add('is-visible');
      };

      const scheduleHide = () => {
        clearTimeout(timer);
        timer = setTimeout(() => {
          popup.classList.remove('is-visible');
        }, 120);
      };

      wrapper.addEventListener('mouseenter', show);
      wrapper.addEventListener('mouseleave', scheduleHide);
      wrapper.addEventListener('focusin', show);
      wrapper.addEventListener('focusout', scheduleHide);
    });
  }

  /* ===================================================================
   * 9. Hero Blade 3D Flip
   * Source: index.tsx lines 174-193
   * =================================================================== */
  function initBladeFlip(scope) {
    const root = scope || document;
    const el = root.querySelector('[data-blade-flip]');
    if (!el) return;
    // CSS handles the transform with :hover, but add a class toggle for
    // browsers/situations where pure CSS hover is unreliable.
    el.addEventListener('mouseenter', () => el.classList.add('is-flipped'));
    el.addEventListener('mouseleave', () => el.classList.remove('is-flipped'));
  }

  /* ===================================================================
   * 11. Variant Selector (Product Page)
   * =================================================================== */
  function initVariantSelector(scope) {
    const root = scope || document;
    const buttons = root.querySelectorAll('[data-variant-btn]');
    if (!buttons.length) return;

    const hiddenInput = root.querySelector('[name="id"]');
    const priceDisplay = root.querySelector('[data-variant-price-display]');

    buttons.forEach((btn) => {
      btn.addEventListener('click', () => {
        // Update selection state
        buttons.forEach((b) => b.classList.remove('is-selected'));
        btn.classList.add('is-selected');

        // Update hidden variant ID
        const variantId = btn.getAttribute('data-variant-id');
        if (hiddenInput && variantId) hiddenInput.value = variantId;

        // Update displayed price
        const price = btn.getAttribute('data-variant-price');
        if (priceDisplay && price) priceDisplay.textContent = price;
      });
    });
  }

  /* ===================================================================
   * 10. Init & Shopify Theme Editor Compatibility
   * =================================================================== */
  var globalsReady = false;
  function initAll(scope) {
    const root = scope || document;
    if (!globalsReady) {
      // One-time listeners (must not be re-bound when the theme editor reloads a section)
      globalsReady = true;
      initHeaderScroll();
      initMobileMenu();
      initAccordions(document);
    }
    initReveal(root);
    initCounters(root);
    initProductGallery(root);
    initCallButtons(root);
    initBladeFlip(root);
    initVariantSelector(root);
  }

  // Boot on DOMContentLoaded
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  // Shopify Theme Editor: reinitialize within the reloaded section
  document.addEventListener('shopify:section:load', (e) => {
    const section = e.target;
    initAll(section);
  });
})();
